import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal } from '@angular/core';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import { TuiButtonModule, TuiDialogContext, TuiHintModule, TuiSvgModule } from '@taiga-ui/core';
import { NgForOf, NgIf, NgOptimizedImage } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { selectShopId, selectShopName } from '@us/web/shop/data-access';
import { CartActions, CartMember, selectCartMembers, selectOrderId } from '@us/web/cart/data-access';
import { Clipboard } from '@angular/cdk/clipboard';
import { MessageService } from '@us/web/shared/feature';
import { QRCodeModule } from 'angularx-qrcode';
import { TuiAvatarModule } from '@taiga-ui/kit';
import { NgForTrackByPropertyDirective } from '@us/web/shared/data-access';
import { selectPersistedUserId } from '@us/web/auth/data-access';

@Component({
    selector: 'us-share-cart-dialog',
    standalone: true,
    imports: [
        NgOptimizedImage,
        TuiButtonModule,
        TranslocoModule,
        TuiSvgModule,
        NgIf,
        QRCodeModule,
        TuiAvatarModule,
        NgForOf,
        NgForTrackByPropertyDirective,
        TuiHintModule
    ],
    templateUrl: './share-cart-dialog.component.html',
    styleUrls: ['./share-cart-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShareCartDialogComponent {
    private readonly context = inject(POLYMORPHEUS_CONTEXT) as TuiDialogContext<void>;
    private readonly store = inject(Store);
    private readonly translocoService = inject(TranslocoService);
    private readonly clipboard = inject(Clipboard);
    private readonly messageService = inject(MessageService);

    private readonly shopId = this.store.selectSignal(selectShopId);
    // TODO: change top cart.shop.name
    private readonly shopName = this.store.selectSignal(selectShopName);
    private readonly orderId = this.store.selectSignal(selectOrderId);
    readonly members: Signal<CartMember[]> = this.store.selectSignal(selectCartMembers);
    readonly currentUserId = this.store.selectSignal(selectPersistedUserId);
    readonly owner = computed(() => this.members().find(member => member.is_owner));
    readonly isOwner = computed(() => this.currentUserId() === this.owner()?.id);

    readonly joinLink = computed(() => `${window.location.origin}/?id=${this.shopId()}&cartId=${this.orderId()}`);

    readonly shareText = computed(() =>
        this.translocoService.translate('share_order_cta', {
            shopName: this.shopName(),
            orderLink: this.joinLink()
        })
    );

    readonly isShowQrCode = signal(false);

    async shareOrder() {
        const text = this.shareText();
        try {
            const shareData: ShareData = { text };
            await navigator.share(shareData);
        } catch (err) {
            if ((err as any).toString() !== 'TypeError: navigator.share is not a function') {
                console.log(`Error: ${err}`);
            }
            this.clipboard.copy(text);
            this.messageService.success(this.translocoService.translate('copied_to_clipboard'));
            this.context.completeWith();
        }
    }

    toggleShowQrCode() {
        this.isShowQrCode.update(value => !value);
    }

    removeMember(id: string) {
        this.store.dispatch(CartActions.removeCartMember({ userId: id }));
    }
}
